"use client";

import { useEffect, useState } from "react";
import { useLocale } from "@/lib/use-i18n";
import { fetchInfraStatusShared } from "@/lib/proxy/infra-status-shared";

type Phase = "normal" | "warm" | "hot" | "critical" | "emergency";

interface Status {
  phase: Phase;
  reasons?: string[];
  message: {
    banner: string | null;
    bannerTone: "info" | "warn" | "critical" | null;
    featureDegraded?: string | null;
  };
}

export function OverloadBanner({
  fetchTimeoutMs = 10_000,
}: {
  /** Bound per intento del poll (default 10s; prop solo para tests). */
  fetchTimeoutMs?: number;
} = {}) {
  const locale = useLocale();
  const [phase, setPhase] = useState<Phase>("normal");
  const [banner, setBanner] = useState<string | null>(null);
  const [tone, setTone] = useState<"info" | "warn" | "critical" | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        // 2026-09-24 FASE 2 FIXER L-30 (slot compartido infra-status, paridad
        // fetchUsageShared): OverloadBanner + StatusDashboard comparten ventana
        // 30s + in-flight por tab — ≤1 hit/30s/tab. Sin no-store (respeta
        // public max-age=60 del server). El timeout corta edges colgados.
        const payload = (await fetchInfraStatusShared({
          headers: { "Accept-Language": locale },
          // 2026-09-23 FASE 4 MEJORA transversal oferta 9/9 P2 (research 2026
          // MDN AbortSignal.timeout 2026-09-01 + jamdesk AbortController guide
          // 2026-04-04: el poll caliente (120s, respeta max-age=60) nunca debe
          // colgar — un edge colgado apilaba fetches entre ticks; el timeout lo
          // corta y el catch silencioso mantiene el banner oculto al reintento.
          signal: AbortSignal.timeout(fetchTimeoutMs),
        })) as Status | null;
        if (!payload || cancelled) return;
        const nextPhase = payload.phase ?? "normal";
        const msg = payload.message;
        // 2026-09-12 aviso de pool al 80%: si la razón es un pool gestionado
        // (búsqueda/gemini/imágenes/db), la cinta suma la línea de degradación
        // (ya traducida ×14) para que el usuario sepa QUÉ está limitado.
        const poolLimited = (payload.reasons ?? []).some((r) =>
          /^(searchPool|geminiPool|imagesPool|dbSize)/.test(r),
        );
        const degraded = msg?.featureDegraded?.trim()
          ? msg.featureDegraded.trim()
          : null;
        const baseBanner = msg?.banner ?? null;
        setPhase(nextPhase);
        setBanner(
          poolLimited && degraded
            ? baseBanner
              ? `${baseBanner} ${degraded}`
              : degraded
            : baseBanner,
        );
        setTone(msg?.bannerTone ?? null);
      } catch {
        // fail silent — banner stays hidden
      }
    };
    fetchStatus();
    // 30s -> 120s: banner de saturación cambia en minutos, no segundos.
    // Respeta Cache-Control del server (public max-age=60) al no usar no-store.
    // 2026-09-21 cero-wakeups en oculta (paridad StatusDashboard): start/stop
    // por visibilitychange — en background no queda timer vivo (antes 1
    // wakeup/120s por tab aunque el tick saltara sin fetch).
    let id = 0;
    const start = () => {
      if (id) return;
      id = window.setInterval(() => void fetchStatus(), 120_000);
    };
    const stop = () => {
      if (id) {
        window.clearInterval(id);
        id = 0;
      }
    };
    const onVis = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        stop();
        return;
      }
      start();
      if (typeof navigator === "undefined" || navigator.onLine) {
        void fetchStatus();
      }
    };
    if (typeof document === "undefined" || !document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [locale, fetchTimeoutMs]);

  // TRACK A-3 2026-09-12 a11y WCAG 4.1.3 (status messages): la live region
  // debe PRE-EXISTIR en el DOM — si solo se monta al aparecer el banner, los
  // lectores pueden no anunciar el incidente. Mismo nodo siempre: en idle es
  // un sr-only vacío (cero impacto visual); al llegar el banner se rellena y
  // se vuelve visible, y el SR lo anuncia.
  if (phase === "normal" || !banner) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid="overload-banner"
      />
    );
  }

  const toneClass =
    tone === "critical"
      ? "border-[var(--danger)]/25 bg-[var(--danger)]/10 text-foreground"
      : tone === "warn"
        ? "border-[var(--warning)]/25 bg-[var(--warning)]/10 text-foreground"
        : "border-[var(--border-strong)] bg-[var(--card)] text-foreground";

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="overload-banner"
      // 2026-09-13 canónico animaciones: solo entrada (sin hook de salida a
      // propósito — la live region idle y el banner son mutuamente excluyentes
      // por WCAG 4.1.3; un desmontaje diferido duplicaría role=status).
      className={`relative w-full border-b px-4 py-2.5 text-center text-[13px] leading-[1.45] animate-in fade-in duration-150 motion-reduce:animate-none ${toneClass}`}
    >
      <p className="mx-auto max-w-3xl text-pretty">{banner}</p>
    </div>
  );
}
